import { Link, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const OVERALL: Record<string, { label: string; color: string; icon: string }> = {
  operational: { label: "All systems operational", color: "#1f7a4d", icon: "check-circle" },
  degraded: { label: "Degraded performance", color: "#9a7b1f", icon: "alert-triangle" },
  major_outage: { label: "Major outage", color: "#b3261e", icon: "siren" },
};
const SEV_COLOR: Record<string, string> = { Major: "#b3261e", Minor: "#9a7b1f" };

function ago(iso: string): string {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 60 * 24) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / (60 * 24))}d ago`;
}

export function StatusPage() {
  const { clientId } = useParams({ from: "/status/$clientId" });
  const { data, isLoading, error } = useQuery({ queryKey: ["status", clientId], queryFn: () => api.status(clientId) });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const o = OVERALL[data.overall] ?? { label: "All systems operational", color: "#1f7a4d", icon: "check-circle" };
  const active = data.incidents.filter((i) => !i.resolved);
  const past = data.incidents.filter((i) => i.resolved);
  const card = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)" } as const;

  return (
    <Page>
      <div>
        <Link to="/incidents" style={{ fontSize: 12, color: "#8a8e88", textDecoration: "none" }}>← Incidents</Link>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginTop: 6 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>{data.client} status</h1>
          <span style={{ fontSize: 12, color: "#8a8e88" }}>public status page · preview</span>
        </div>
      </div>

      {/* Overall banner */}
      <div style={{ ...card, boxShadow: `inset 0 0 0 1px ${o.color}55`, padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ width: 34, height: 34, borderRadius: "50%", background: `${o.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={o.icon} size={18} color={o.color} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: o.color }}>{o.label}</div>
          <div style={{ fontSize: 12, color: "#8a8e88" }}>{active.length === 0 ? "No active incidents." : `${active.length} active incident${active.length === 1 ? "" : "s"}.`}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>{data.subscribers.total} subscribers</div>
          <div style={{ fontSize: 11, color: "#8a8e88" }}>{data.subscribers.email} email · {data.subscribers.sms} SMS</div>
        </div>
      </div>

      {/* Active incidents */}
      {active.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {active.map((inc) => (
            <div key={inc.id} style={{ ...card, boxShadow: "inset 0 0 0 1px rgba(179,38,30,.35)", padding: "14px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{inc.title}</span>
                <span style={{ fontSize: 10.5, fontWeight: 600, color: SEV_COLOR[inc.severity] ?? "#8a8e88" }}>{inc.severity}</span>
                <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#fbf1dd", color: "#9a7b1f" }}>{inc.status}</span>
              </div>
              <div style={{ marginTop: 8, borderLeft: "2px solid #f0efe9", paddingLeft: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {inc.updates.map((u, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 11.5 }}><span style={{ fontWeight: 600 }}>{u.status}</span> <span style={{ color: "#a8aca6" }}>· {ago(u.at)}</span></div>
                    <div style={{ fontSize: 12.5, color: "#4a504a", marginTop: 1 }}>{u.body}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Resolved history */}
      {past.length > 0 && (
        <div style={{ ...card, padding: "6px 0" }}>
          <div style={{ padding: "10px 18px 6px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#a8aca6" }}>Recent history</div>
          {past.map((inc) => (
            <div key={inc.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 18px", borderTop: "1px solid #f4f3ee" }}>
              <Icon name="check-circle" size={14} color="#1f7a4d" />
              <span style={{ fontSize: 12.5, flex: 1 }}>{inc.title}</span>
              <span style={{ fontSize: 11, color: "#8a8e88" }}>Resolved{inc.updates[0] ? ` · ${ago(inc.updates[0].at)}` : ""}</span>
            </div>
          ))}
        </div>
      )}

      {data.incidents.length === 0 && <div style={{ ...card, padding: "14px 18px", fontSize: 12.5, color: "#8a8e88" }}>No incident history.</div>}
    </Page>
  );
}
